import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const soupRecipes: RecipeFamily[] = [
  {
    id: "miso-tofu-noodle-soup",
    nameTemplate: "{broth.name} {protein.name} & {noodle.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 20,
    slots: [
      slot("broth", "Broth base", true, "miso", [
        option("miso", 3, "tbsp", undefined, 3, undefined, {
          tr: { preparation: undefined },
        }),
        option("soy-sauce", 3, "tbsp", undefined, 1),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "cut into small cubes", 3, undefined, {
          tr: { preparation: "küçük küpler halinde doğranmış" },
        }),
        option("edamame", 150, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu çıkarılmış" },
        }),
      ]),
      slot("noodle", "Noodle", true, "noodles", [
        option("noodles", 160, "g", "cooked", 3, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
        option("spaghetti", 160, "g", "cooked", 1, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("greens", "Greens", true, "bok-choy", [
        option("bok-choy", 200, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("spinach", 150, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      slot("finish", "Finish", false, "scallion", [
        option("scallion", 3, "piece", "thinly sliced", 3, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
        option("sesame-seeds", 1, "tbsp", undefined, 2),
      ]),
      pantry.water(800),
      pantry.salt,
    ],
    steps: [
      {
        id: "make-broth",
        template:
          "Bring {water.name} to a gentle simmer in a pot. Whisk in {broth.name} until dissolved.",
        translations: {
          tr: {
            template:
              "Bir tencerede {water.name} hafif kaynamaya başlayana kadar ısıtın. {broth.name} ekleyerek çırpın.",
          },
        },
      },
      {
        id: "add-protein-greens",
        template:
          "Add {protein.name} and {greens.name}. Simmer for 3 minutes until the greens are wilted.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {greens.name} ekleyin. Yeşillikler solana kadar 3 dakika pişirin.",
          },
        },
      },
      {
        id: "add-noodles",
        template:
          "Divide {noodle.name} between bowls. Ladle the hot broth and {protein.name} over the noodles.",
        translations: {
          tr: {
            template:
              "{noodle.name} kaselere paylaştırın. Sıcak suyu ve {protein.name} eriştelerin üzerine dökün.",
          },
        },
      },
      {
        id: "finish",
        template: "Top with {finish.name} if using and serve immediately.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ile süsleyin ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "protein", "starch", "bitter", "salt"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{broth.name} {protein.name} & {noodle.name} çorbası",
      },
    },
  },
  {
    id: "sweet-potato-coconut-soup",
    nameTemplate: "Creamy {vegetable.name} & {ginger.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "sweet-potato", [
        option("sweet-potato", 500, "g", "cut into 2 cm cubes", 3, undefined, {
          tr: { preparation: "2 cm'lik küpler halinde doğranmış" },
        }),
        option("carrot", 450, "g", "sliced", 2, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
      ]),
      slot("cream", "Creamy element", true, "coconut-milk", [
        option("coconut-milk", 300, "ml"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lime", [
        option("lime", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("cilantro", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      pantry.water(400),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Warm {oil.name} in a pot over medium heat. Add {onion.name}, {garlic.name}, and {ginger.name}; soften for 5 minutes.",
        translations: {
          tr: {
            template:
              "Bir tencerede {oil.name} orta ateşte ısıtın. {onion.name}, {garlic.name} ve {ginger.name} ekleyin; 5 dakika yumuşatın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {vegetable.name}, {water.name}, {salt.name}, and {black-pepper.name}. Cover and simmer for 20 minutes until the vegetable is very tender.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kapağını kapatıp sebze çok yumuşayana kadar 20 dakika pişirin.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Remove from heat and blend until completely smooth. Stir in {cream.name} and reheat gently.",
        translations: {
          tr: {
            template:
              "Ocaktan alın ve tamamen pürüzsüz olana kadar blendırda çekin. {cream.name} ekleyin ve hafifçe ısıtın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze in {finish.name} if using. Taste and adjust seasoning, then serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} sıkın. Tadına bakın ve baharatı ayarlayıp servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "sweet", "fat", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Kremalı {vegetable.name} & {ginger.name} çorbası",
      },
    },
  },
  {
    id: "white-bean-kale-soup",
    nameTemplate: "{protein.name} & {greens.name} soup with {oil.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 32,
    slots: [
      slot("protein", "Protein", true, "white-beans", [
        option("white-beans", 480, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("chickpeas", 480, "g", "drained", 2, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 180, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("broccoli", 250, "g", "cut into small florets", 1, undefined, {
          tr: { preparation: "küçük demetlere ayrılmış" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("parsley", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      pantry.water(600),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Warm {oil.name} in a pot over medium heat. Soften {onion.name} for 5 minutes, then add {garlic.name} and stir for 30 seconds.",
        translations: {
          tr: {
            template:
              "Bir tencerede {oil.name} orta ateşte ısıtın. {onion.name} 5 dakika yumuşatın, ardından {garlic.name} ekleyip 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "build-soup",
        template:
          "Add {protein.name}, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a gentle simmer and cook for 10 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Hafif kaynamaya getirip 10 dakika pişirin.",
          },
        },
      },
      {
        id: "add-greens",
        template:
          "Add {greens.name} and cook for 3 to 4 minutes until wilted and tender.",
        translations: {
          tr: {
            template:
              "{greens.name} ekleyin ve 3-4 dakika solana kadar pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Drizzle generously with extra {oil.name} and add {finish.name} if using. Taste for seasoning.",
        translations: {
          tr: {
            template:
              "Üzerine bol {oil.name} gezdirin ve kullanıyorsanız {finish.name} ekleyin. Tadına bakın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "bulk", "fat", "bitter", "acid"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} & {greens.name} çorbası ve {oil.name}",
      },
    },
  },
  {
    id: "roasted-pepper-tomato-soup",
    nameTemplate: "Roasted {pepper.name} & {tomato.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 45,
    slots: [
      slot("pepper", "Sweet pepper", true, "bell-pepper", [
        option("bell-pepper", 400, "g", "seeded and quartered", 3, undefined, {
          tr: { preparation: "tohumları çıkarılmış ve dörde bölünmüş" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "halved", 3, undefined, {
          tr: { preparation: "ikiye kesilmiş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "quartered", 1, undefined, {
          tr: { preparation: "dörde bölünmüş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "left whole", 1, undefined, {
          tr: { preparation: "bütün" },
        }),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "balsamic-vinegar", [
        option("balsamic-vinegar", 1, "tbsp", undefined, 3),
        option("basil", 15, "g", "torn", 2, undefined, {
          tr: { preparation: "parçalanmış" },
        }),
      ]),
      pantry.water(400),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {pepper.name}, {tomato.name}, {onion.name}, and {garlic.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Spread on a baking sheet and roast for 30 to 35 minutes until lightly charred.",
        translations: {
          tr: {
            template:
              "{pepper.name}, {tomato.name}, {onion.name} ve {garlic.name} üzerine {oil.name}, {salt.name} ve {black-pepper.name} gezdirip karıştırın. Tepsiye yayın ve hafif yanana kadar 30-35 dakika kızartın.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Add the roasted vegetables to a blender with {water.name} and {spice.name} if using. Blend until smooth.",
        translations: {
          tr: {
            template:
              "Kızartılmış sebzeleri {water.name} ve kullanıyorsanız {spice.name} ile blendıra ekleyin. Pürüzsüz olana kadar çekin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Transfer to a pot and warm over low heat. Add {finish.name} if using, and taste for seasoning.",
        translations: {
          tr: {
            template:
              "Bir tencereye alın ve kısık ateşte ısıtın. Kullanıyorsanız {finish.name} ekleyin ve tadına bakın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "sweet", "fat", "acid", "umami"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırında {pepper.name} & {tomato.name} çorbası",
      },
    },
  },
  {
    id: "mushroom-barley-stew",
    nameTemplate: "Hearty {vegetable.name} & {grain.name} stew",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 45,
    slots: [
      slot("vegetable", "Vegetable", true, "mushroom", [
        option("mushroom", 350, "g", "thickly sliced", 3, undefined, {
          tr: { preparation: "kalın dilimlenmiş" },
        }),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 2, undefined, {
          tr: { preparation: "2 cm'lik küpler halinde doğranmış" },
        }),
      ]),
      slot("grain", "Grain", true, "barley", [
        option("barley", 100, "g", "rinsed", 3, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
        option("farro", 100, "g", "rinsed", 2, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("umami-boost", "Umami boost", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
        option("miso", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("cilantro", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      pantry.water(900),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "brown-mushrooms",
        template:
          "Warm {oil.name} in a wide pot over high heat. Add {vegetable.name} and cook without stirring for 4 minutes until well browned. Stir and cook 2 minutes more.",
        translations: {
          tr: {
            template:
              "{oil.name} geniş bir tencerede yüksek ateşte ısıtın. {vegetable.name} ekleyin ve karıştırmadan 4 dakika iyi kızarana kadar pişirin. Karıştırıp 2 dakika daha pişirin.",
          },
        },
      },
      {
        id: "soften-aromatics",
        template:
          "Reduce heat to medium. Add {onion.name} and {garlic.name}; soften for 4 minutes.",
        translations: {
          tr: {
            template:
              "Ateşi orta kısın. {onion.name} ve {garlic.name} ekleyin; 4 dakika yumuşatın.",
          },
        },
      },
      {
        id: "simmer-grain",
        template:
          "Add {grain.name}, {umami-boost.name}, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a boil, then reduce to a simmer. Cook for 25 to 30 minutes until the grain is tender.",
        translations: {
          tr: {
            template:
              "{grain.name}, {umami-boost.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kaynatın, ardından kısın. Tahıl yumuşayana kadar 25-30 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {finish.name} if using. Taste and serve in deep bowls.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin. Tadına bakın ve derin kaselerde servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "starch", "bulk", "fat", "salt"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Doyurucu {vegetable.name} & {grain.name} güveci",
      },
    },
  },
  {
    id: "spiced-chickpea-spinach-soup",
    nameTemplate: "Spiced {protein.name} & {greens.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 480, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("lentils", 160, "g", "rinsed", 2, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 200, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("broccoli", 250, "g", "cut into small florets", 1, undefined, {
          tr: { preparation: "küçük demetlere ayrılmış" },
        }),
      ]),
      slot("tomato", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed", 3, undefined, {
          tr: { preparation: "ezilmiş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      pantry.water(300),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "bloom-spice",
        template:
          "Warm {oil.name} in a pot. Add {onion.name} and cook for 4 minutes. Stir in {garlic.name} and {spice.name}; cook for 1 minute until fragrant.",
        translations: {
          tr: {
            template:
              "Tencerede {oil.name} ısıtın. {onion.name} ekleyin ve 4 dakika pişirin. {garlic.name} ve {spice.name} ekleyin; 1 dakika kokusu çıkana kadar pişirin.",
          },
        },
      },
      {
        id: "build-soup",
        template:
          "Add {tomato.name}, {protein.name}, {water.name}, {salt.name}, and {black-pepper.name}. Simmer for 12 minutes.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {protein.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. 12 dakika pişirin.",
          },
        },
      },
      {
        id: "add-greens",
        template:
          "Stir in {greens.name} and cook for 2 to 3 minutes until wilted.",
        translations: {
          tr: {
            template:
              "{greens.name} ekleyin ve 2-3 dakika solana kadar pişirin.",
          },
        },
      },
      {
        id: "finish",
        template: "Add {finish.name} if using, taste for seasoning, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin, tadına bakın ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "bulk", "acid", "umami", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Baharatlı {protein.name} & {greens.name} çorbası",
      },
    },
  },
  {
    id: "tomato-chickpea-soup",
    nameTemplate: "{vegetable.name} & {protein.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "tomato", [
        option("tomato", 500, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("bell-pepper", 450, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("white-beans", 240, "g", "drained", 2, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("black-beans", 240, "g", "drained", 1, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("spice", "Warm spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp"),
        option("cumin", 1, "tsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      pantry.water(600),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Warm {oil.name} in a deep pot. Add {onion.name} and cook for 5 minutes, then stir in {garlic.name}.",
        translations: {
          tr: {
            template:
              "{oil.name} derin bir tencerede ısıtın. {onion.name} ekleyin ve 5 dakika pişirin, ardından {garlic.name} ekleyin.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Add {vegetable.name}, {salt.name}, and {black-pepper.name}. Stir in {spice.name} if using, then cook until the vegetables begin to soften.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {salt.name} ve {black-pepper.name} ekleyin. Kullanıyorsanız {spice.name} de ekleyin, ardından sebzeler yumuşamaya başlayana kadar pişirin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {protein.name} and {water.name}. Simmer gently for 18 to 20 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {water.name} ekleyin. 18-20 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Crush a few of the beans against the pot for body. Finish with {finish.name} if using, then taste for seasoning.",
        translations: {
          tr: {
            template:
              "Çorbaya kıvam vermek için fasulyelerin birkaçını tencerenin kenarında ezin. Kullanıyorsanız {finish.name} ile tamamlayın, ardından tadına bakıp baharatını ayarlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "fat", "acid", "umami", "aroma"],
    editorialPriority: 10,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: { nameTemplate: "{vegetable.name} ve {protein.name} çorbası" },
    },
  },
  {
    id: "tofu-tomato-onion-soup",
    nameTemplate: "Spiced {protein.name} & {vegetable.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 32,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3, undefined, {
          tr: { preparation: "suyu sıkılmış ve küp doğranmış" },
        }),
        option("chickpeas", 240, "g", "drained", 2, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("vegetable", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("bell-pepper", 350, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("spice", "Warm spice", false, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      pantry.water(500),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Warm {oil.name} in a deep pot over medium heat. Add {onion.name} and cook for 5 minutes until soft, then stir in {garlic.name} and {spice.name} if using for 30 seconds.",
        translations: {
          tr: {
            template:
              "{oil.name} derin bir tencerede orta ateşte ısıtın. {onion.name} ekleyip 5 dakika yumuşayana kadar pişirin, ardından {garlic.name} ve kullanıyorsanız {spice.name} ekleyip 30 saniye daha karıştırın.",
          },
        },
      },
      {
        id: "build-soup",
        template:
          "Add {vegetable.name}, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a simmer and cook for 10 minutes until the tomatoes break down.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kaynamaya bırakıp domatesler dağılana kadar 10 dakika pişirin.",
          },
        },
      },
      {
        id: "add-protein",
        template:
          "Add {protein.name} and simmer for 8 minutes until heated through and the soup has thickened.",
        translations: {
          tr: {
            template:
              "{protein.name} ekleyip iyice ısınana ve çorba koyulaşana kadar 8 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {finish.name} if using, taste for seasoning, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyip karıştırın, tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "bulk", "acid", "umami", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Baharatlı {protein.name} & {vegetable.name} çorbası",
      },
    },
  },
  {
    id: "carrot-lentil-soup",
    nameTemplate: "Golden {vegetable.name} & {protein.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 38,
    slots: [
      slot("vegetable", "Main vegetable", true, "carrot", [
        option("carrot", 350, "g", "sliced", 3, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("sweet-potato", 350, "g", "cut into small cubes", 2, undefined, {
          tr: { preparation: "küçük küpler halinde doğranmış" },
        }),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 160, "g", "rinsed", 3, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
        option("chickpeas", 240, "g", "drained", 1, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("cilantro", 15, "g", "chopped", 1, undefined, {
          tr: { preparation: "kıyılmış" },
        }),
      ]),
      pantry.water(700),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soften-onion",
        template:
          "Warm {oil.name} in a pot and soften {onion.name} for 5 minutes.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede ısıtın ve {onion.name} 5 dakika boyunca yumuşayana kadar pişirin.",
          },
        },
      },
      {
        id: "toast-spice",
        template:
          "Stir in {spice.name}, then add {vegetable.name}, {protein.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{spice.name} ekleyip karıştırın, ardından {vegetable.name}, {protein.name}, {salt.name} ve {black-pepper.name} ekleyin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Pour in {water.name}. Cover loosely and simmer for 25 minutes, until the lentils and vegetables are tender.",
        translations: {
          tr: {
            template:
              "{water.name} ekleyin. Tencerenin kapağını hafif aralık kapatıp mercimek ve sebzeler yumuşayana kadar 25 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Blend part of the soup for a creamy texture. Add {finish.name} if using and adjust the seasoning.",
        translations: {
          tr: {
            template:
              "Kremsi bir doku için çorbanın bir kısmını blenderdan geçirin. Kullanıyorsanız {finish.name} ekleyip tadını ayarlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "sweet", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "Altın renkli {vegetable.name} & {protein.name} çorbası",
      },
    },
  },
  {
    id: "potato-greens-stew",
    nameTemplate: "Creamy {base.name} & {greens.name} stew",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 40,
    slots: [
      slot("base", "Hearty base", true, "potato", [
        option("potato", 450, "g", "cut into 2 cm cubes", 3, undefined, {
          tr: { preparation: "2 cm küpler halinde doğranmış" },
        }),
        option("sweet-potato", 450, "g", "cut into 2 cm cubes", 2, undefined, {
          tr: { preparation: "2 cm küpler halinde doğranmış" },
        }),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 180, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("broccoli", 250, "g", "cut into small florets", 1, undefined, {
          tr: { preparation: "küçük parçalara ayrılmış" },
        }),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
        option("chickpeas", 240, "g", "drained", 2, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("aromatic", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 3, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
        option("ginger", 20, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("cream", "Creamy element", true, "coconut-milk", [
        option("coconut-milk", 250, "ml", undefined, 3),
        option("yogurt", 180, "g", undefined, 1),
      ]),
      slot("spice", "Spice", true, "cumin", [option("cumin", 1, "tsp")]),
      pantry.water(350),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "start-stew",
        template:
          "Add {aromatic.name}, {base.name}, {spice.name}, {water.name}, {salt.name}, and {black-pepper.name} to a wide pot.",
        translations: {
          tr: {
            template:
              "{aromatic.name}, {base.name}, {spice.name}, {water.name}, {salt.name} ve {black-pepper.name} geniş bir tencereye ekleyin.",
          },
        },
      },
      {
        id: "simmer-base",
        template:
          "Bring to a simmer, cover, and cook for 15 minutes, until {base.name} is almost tender.",
        translations: {
          tr: {
            template:
              "Kaynamaya bırakın, kapağını kapatıp {base.name} neredeyse yumuşayana kadar 15 dakika pişirin.",
          },
        },
      },
      {
        id: "add-greens",
        template:
          "Add {greens.name} and {protein.name}. Continue cooking until the vegetables are tender.",
        translations: {
          tr: {
            template:
              "{greens.name} ve {protein.name} ekleyin. Sebzeler yumuşayana kadar pişirmeye devam edin.",
          },
        },
      },
      {
        id: "make-creamy",
        template:
          "Lower the heat and stir in {cream.name}. Warm gently without boiling, then taste and serve.",
        translations: {
          tr: {
            template:
              "Ateşi kısın ve {cream.name} ekleyip karıştırın. Kaynatmadan hafifçe ısıtın, ardından tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "bitter", "aroma"],
    editorialPriority: 8,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "Kremalı {base.name} & {greens.name} yahnisi",
      },
    },
  },
  {
    id: "oven-roasted-tomato-soup",
    nameTemplate: "Oven-roasted {vegetable.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 45,
    slots: [
      slot("vegetable", "Roasting vegetable", true, "tomato", [
        option("tomato", 600, "g", "halved", 3, undefined, {
          tr: { preparation: "ikiye kesilmiş" },
        }),
        option("bell-pepper", 550, "g", "seeded and quartered", 2, undefined, {
          tr: { preparation: "çekirdekleri çıkarılmış ve dörde bölünmüş" },
        }),
      ]),
      slot("protein", "Body", true, "chickpeas", [
        option("chickpeas", 180, "g", "drained", 3, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
        option("lentils", 180, "g", "cooked", 1, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "quartered", 1, undefined, {
          tr: { preparation: "dörde bölünmüş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "left whole", 1, undefined, {
          tr: { preparation: "bütün bırakılmış" },
        }),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Finish", false, "balsamic-vinegar", [
        option("balsamic-vinegar", 1, "tbsp", undefined, 3),
        option("maple-syrup", 1, "tbsp", undefined, 2),
        option("lemon", 1, "piece", "juiced", 1, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      pantry.water(450),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 210°C.",
        translations: {
          tr: { template: "Fırını 210°C'ye ısıtın." },
        },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name}, {onion.name}, {garlic.name}, and {protein.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Roast for 28 to 32 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {onion.name}, {garlic.name} ve {protein.name} malzemelerini {oil.name}, {salt.name} ve {black-pepper.name} ile karıştırın. 28 ila 32 dakika fırınlayın.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Transfer the roasted mixture to a heat-safe blender with {water.name}. Blend until smooth, venting the lid carefully.",
        translations: {
          tr: {
            template:
              "Fırınlanmış karışımı {water.name} ile birlikte ısıya dayanıklı bir blendera aktarın. Kapağını dikkatlice aralık bırakarak pürüzsüz olana kadar karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {finish.name} if using. Add a splash of hot water if needed and check the seasoning.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyip karıştırın. Gerekirse biraz sıcak su ekleyip tadına bakın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "fat", "acid", "umami"],
    editorialPriority: 9,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "Fırında kavrulmuş {vegetable.name} çorbası",
      },
    },
  },
  {
    id: "tomato-gazpacho",
    nameTemplate: "Chilled {vegetable.name} & {fresh.name} gazpacho",
    foodType: "soup-stew",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("vegetable", "Tomato base", true, "tomato", [
        option("tomato", 600, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("fresh", "Cool vegetable", true, "cucumber", [
        option("cucumber", 250, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("bell-pepper", 250, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [option("garlic", 1, "piece")]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3, undefined, {
          tr: { preparation: "kıyılmış" },
        }),
        option("cilantro", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "kıyılmış" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "blend",
        template:
          "Add {vegetable.name}, {fresh.name}, {garlic.name}, {acid.name}, {oil.name}, {salt.name}, and {black-pepper.name} to a blender.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {fresh.name}, {garlic.name}, {acid.name}, {oil.name}, {salt.name} ve {black-pepper.name} malzemelerini bir blendera ekleyin.",
          },
        },
      },
      {
        id: "blend-smooth",
        template:
          "Blend on high until completely smooth. Taste and adjust the seasoning.",
        translations: {
          tr: {
            template:
              "Tamamen pürüzsüz olana kadar yüksek hızda karıştırın. Tadına bakıp baharatını ayarlayın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Pour into bowls, garnish with {herb.name} if using, and serve immediately or refrigerate for up to 4 hours.",
        translations: {
          tr: {
            template:
              "Kaselere dökün, kullanıyorsanız {herb.name} ile süsleyin ve hemen servis yapın ya da 4 saate kadar buzdolabında bekletin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["acid", "fat", "fresh", "umami"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Soğuk {vegetable.name} & {fresh.name} gazpaçosu",
      },
    },
  },
  {
    id: "chilled-avocado-cucumber-soup",
    nameTemplate: "Chilled {base.name} & {veg.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("base", "Creamy base", true, "avocado", [
        option("avocado", 2, "piece", "pitted and peeled", 3, undefined, {
          tr: { preparation: "çekirdeği çıkarılmış ve soyulmuş" },
        }),
      ]),
      slot("veg", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 1, "piece", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("zucchini", 200, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 1, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 1, "piece", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      pantry.water(250),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "blend",
        template:
          "Add {base.name}, {veg.name}, {garlic.name}, {acid.name}, {oil.name}, {water.name}, {salt.name}, and {black-pepper.name} to a blender. Blend until completely smooth.",
        translations: {
          tr: {
            template:
              "{base.name}, {veg.name}, {garlic.name}, {acid.name}, {oil.name}, {water.name}, {salt.name} ve {black-pepper.name} malzemelerini bir blendera ekleyin. Tamamen pürüzsüz olana kadar karıştırın.",
          },
        },
      },
      {
        id: "adjust",
        template:
          "Taste and adjust seasoning. Add a splash more {water.name} if the soup is too thick and blend again.",
        translations: {
          tr: {
            template:
              "Tadına bakıp baharatını ayarlayın. Çorba çok koyuysa biraz daha {water.name} ekleyip tekrar karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Pour into bowls and top with {herb.name} if using. Serve immediately.",
        translations: {
          tr: {
            template:
              "Kaselere dökün ve kullanıyorsanız üzerine {herb.name} serpin. Hemen servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fat", "fresh", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Soğuk {base.name} & {veg.name} çorbası",
      },
    },
  },
  {
    id: "roasted-eggplant-lentil-stew",
    nameTemplate: "Slow-baked {vegetable.name} & {protein.name} stew",
    foodType: "soup-stew",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 52,
    slots: [
      slot("vegetable", "Main vegetable", true, "eggplant", [
        option("eggplant", 350, "g", "cut into 2 cm cubes", 3, undefined, {
          tr: { preparation: "2 cm küpler halinde doğranmış" },
        }),
        option(
          "zucchini",
          350,
          "g",
          "cut into thick half-moons",
          2,
          undefined,
          {
            tr: { preparation: "kalın yarım aylar halinde doğranmış" },
          },
        ),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 160, "g", "rinsed", 3, undefined, {
          tr: { preparation: "durulanmış" },
        }),
        option("chickpeas", 240, "g", "drained", 2, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed", 1, undefined, {
          tr: { preparation: "ezilmiş" },
        }),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      pantry.water(450),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "combine",
        template:
          "In a deep baking dish, combine {vegetable.name}, {protein.name}, {sauce.name}, {onion.name}, {garlic.name}, {spice.name}, {oil.name}, {water.name}, {salt.name}, and {black-pepper.name}. Stir well.",
        translations: {
          tr: {
            template:
              "Derin bir fırın kabında {vegetable.name}, {protein.name}, {sauce.name}, {onion.name}, {garlic.name}, {spice.name}, {oil.name}, {water.name}, {salt.name} ve {black-pepper.name} malzemelerini birleştirin. İyice karıştırın.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Cover tightly with foil and bake for 35 to 40 minutes, until {vegetable.name} is completely tender and the sauce is thick.",
        translations: {
          tr: {
            template:
              "Sıkıca folyo ile kapatıp {vegetable.name} tamamen yumuşayana ve sos koyulaşana kadar 35-40 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir well and add {finish.name} if using. Taste for seasoning and serve.",
        translations: {
          tr: {
            template:
              "İyice karıştırın ve kullanıyorsanız {finish.name} ekleyin. Tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Fırında ağır pişmiş {vegetable.name} & {protein.name} yahnisi",
      },
    },
  },
  {
    id: "corn-black-bean-soup",
    nameTemplate: "{vegetable.name} & {protein.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "corn", [
        option("corn", 400, "g", "kernels, fresh or frozen", 3, undefined, {
          tr: { preparation: "taze veya donmuş taneler" },
        }),
        option("zucchini", 350, "g", "diced", 2, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 400, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("chickpeas", 400, "g", "drained", 2, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
      ]),
      slot("onion", "Aromatic base", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed", 1, undefined, {
          tr: { preparation: "ezilmiş" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1.5, "tsp", undefined, 3),
        option("smoked-paprika", 1.5, "tsp", undefined, 2),
        option("chili-flakes", 0.5, "tsp", undefined, 1),
      ]),
      slot("acid", "Bright acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lemon", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.water(700),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a large pot over medium heat. Cook {onion.name} for 5 minutes until soft.",
        translations: {
          tr: {
            template:
              "Büyük bir tencerede {oil.name} orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin.",
          },
        },
      },
      {
        id: "spice",
        template: "Add {garlic.name} and {spice.name}; stir for 1 minute.",
        translations: {
          tr: {
            template:
              "{garlic.name} ve {spice.name} ekleyin; 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {vegetable.name}, {protein.name}, {sauce.name}, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a boil, then simmer for 20 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name}, {sauce.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kaynatın, ardından 20 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {acid.name} and {herb.name} if using. Taste for seasoning and serve.",
        translations: {
          tr: {
            template:
              "{acid.name} ve kullanıyorsanız {herb.name} karıştırın. Tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "bulk", "acid", "aroma", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{vegetable.name} & {protein.name} çorbası",
      },
    },
  },
  {
    id: "white-bean-spinach-soup",
    nameTemplate: "{protein.name} & {greens.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 30,
    slots: [
      slot("protein", "Protein", true, "white-beans", [
        option("white-beans", 400, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("chickpeas", 400, "g", "drained", 2, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("lentils", 160, "g", "rinsed", 1, undefined, {
          tr: { preparation: "durulanmış" },
        }),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 120, "g", "roughly torn", 3, undefined, {
          tr: { preparation: "iri parçalanmış" },
        }),
        option("cabbage", 200, "g", "shredded", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("onion", "Aromatic base", true, "onion", [
        option("onion", 1, "piece", "diced", 1, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely sliced", 1, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("sauce", "Sauce base", false, "tomato", [
        option("tomato", 200, "g", "crushed", 1, undefined, {
          tr: { preparation: "ezilmiş" },
        }),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 0.5, "tsp"),
      ]),
      slot("acid", "Bright acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("basil", 10, "g", "torn", 2, undefined, {
          tr: { preparation: "elle parçalanmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.water(600),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a pot over medium heat. Cook {onion.name} for 4 minutes. Add {garlic.name} and {heat.name} if using; stir 30 seconds.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede orta ateşte ısıtın. {onion.name} 4 dakika pişirin. {garlic.name} ve kullanıyorsanız {heat.name} ekleyin; 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {protein.name}, {sauce.name} if using, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a boil and simmer for 15 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name}, kullanıyorsanız {sauce.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kaynatın ve 15 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "wilt-greens",
        template: "Stir in {greens.name} and cook for 2 minutes until wilted.",
        translations: {
          tr: {
            template:
              "{greens.name} karıştırın ve pörsüyene kadar 2 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Add {acid.name} and {herb.name} if using. Taste for seasoning and serve.",
        translations: {
          tr: {
            template:
              "{acid.name} ve kullanıyorsanız {herb.name} ekleyin. Tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fresh", "acid", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} & {greens.name} çorbası",
      },
    },
  },
  {
    id: "creamy-broccoli-soup",
    nameTemplate: "Creamy {vegetable.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 30,
    slots: [
      slot("vegetable", "Main vegetable", true, "broccoli", [
        option("broccoli", 400, "g", "florets", 3, undefined, {
          tr: { preparation: "çiçekler halinde ayrılmış" },
        }),
        option("cauliflower", 400, "g", "florets", 2, undefined, {
          tr: { preparation: "çiçekler halinde ayrılmış" },
        }),
      ]),
      slot("cream", "Cream", true, "coconut-milk", [
        option("coconut-milk", 200, "ml"),
      ]),
      slot("onion", "Aromatic base", true, "onion", [
        option("onion", 1, "piece", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("spice", "Spice", false, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("acid", "Bright finish", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lime", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      pantry.water(400),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a pot over medium heat. Cook {onion.name} for 4 minutes until soft. Add {garlic.name} and {spice.name} if using; stir 30 seconds.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede orta ateşte ısıtın. {onion.name} yumuşayana kadar 4 dakika pişirin. {garlic.name} ve kullanıyorsanız {spice.name} ekleyin; 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {vegetable.name}, {water.name}, {salt.name}, and {black-pepper.name}. Bring to a boil and simmer for 12 minutes until {vegetable.name} is very tender.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {water.name}, {salt.name} ve {black-pepper.name} ekleyin. Kaynatın ve {vegetable.name} çok yumuşayana kadar 12 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Remove from heat. Add {cream.name} and blend until smooth with a stick blender, or transfer in batches to a blender.",
        translations: {
          tr: {
            template:
              "Ocaktan alın. {cream.name} ekleyin ve bir blender ile pürüzsüz olana kadar karıştırın, ya da parti parti bir blendere aktarın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Return to low heat to warm through. Stir in {acid.name}, taste for seasoning, and serve.",
        translations: {
          tr: {
            template:
              "Isınması için kısık ateşe geri koyun. {acid.name} karıştırın, tadına bakıp servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fresh", "fat", "acid", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Kremalı {vegetable.name} çorbası",
      },
    },
  },
  {
    id: "turkish-spiced-lentil-soup",
    nameTemplate: "Turkish-spiced {protein.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("protein", "Legume", true, "lentils", [
        option("lentils", 180, "g", "rinsed", 3, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
        option("chickpeas", 240, "g", "drained", 2, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("onion", "Aromatic base", true, "onion", [
        option("onion", 1, "piece", "finely chopped", 3, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely chopped", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("sauce", "Spiced paste", true, "red-pepper-paste", [
        option("red-pepper-paste", 2, "tbsp", undefined, 3),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1.5, "tsp", undefined, 3),
        option("allspice", 1, "tsp", undefined, 2),
      ]),
      slot("acid", "Bright finish", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("red-wine-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("garnish", "Dried herb garnish", false, "dried-mint", [
        option("dried-mint", 0.5, "tsp", undefined, 3),
        option("sumac", 0.5, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.water(850),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a large pot over medium heat. Cook {onion.name} for 5 minutes until soft. Add {garlic.name} and {spice.name}; stir 1 minute. Add {sauce.name} and stir 1 minute more.",
        translations: {
          tr: {
            template:
              "{oil.name} büyük bir tencerede orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin. {garlic.name} ve {spice.name} ekleyin; 1 dakika karıştırın. {sauce.name} ekleyip 1 dakika daha karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {protein.name} and {water.name}. Bring to a boil, reduce heat, and simmer for 20 minutes until {protein.name} is very soft. Season with {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {water.name} ekleyin. Kaynatın, ateşi kısın ve {protein.name} çok yumuşayana kadar 20 dakika pişirin. {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "blend",
        template:
          "Use a stick blender to blend about half the soup until smooth, or transfer half to a blender and return to the pot. Stir to combine.",
        translations: {
          tr: {
            template:
              "Bir blender ile çorbanın yaklaşık yarısını pürüzsüz olana kadar karıştırın, ya da yarısını bir blendere aktarıp tencereye geri koyun. Karıştırarak birleştirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {acid.name}. Ladle into bowls, drizzle with a little {oil.name}, and sprinkle over {garnish.name} if using.",
        translations: {
          tr: {
            template:
              "{acid.name} karıştırın. Kaselere paylaştırın, üzerine biraz {oil.name} gezdirin ve kullanıyorsanız {garnish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "aroma", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Türk usulü baharatlı {protein.name} çorbası",
      },
    },
  },
  {
    id: "lemongrass-coconut-soup",
    nameTemplate: "Lemongrass & {protein.name} coconut soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3, undefined, {
          tr: { preparation: "suyu sıkılmış ve küp doğranmış" },
        }),
        option("chickpeas", 240, "g", "drained and rinsed", 2, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
      ]),
      slot("lemongrass", "Lemongrass", true, "lemongrass", [
        option(
          "lemongrass",
          2,
          "piece",
          "bruised and cut into batons",
          1,
          undefined,
          { tr: { preparation: "ezilmiş ve baton şeklinde kesilmiş" } },
        ),
      ]),
      slot("coconut", "Coconut milk", true, "coconut-milk", [
        option("coconut-milk", 400, "ml"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely sliced", 1, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("acid", "Acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lemon", 1, "piece", "juiced", 1, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("vegetable", "Vegetable", false, "spinach", [
        option("spinach", 80, "g", undefined, 3),
        option("bok-choy", 200, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("broccoli", 200, "g", "cut into small florets", 1, undefined, {
          tr: { preparation: "küçük çiçekler halinde kesilmiş" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("scallion", 2, "piece", "sliced", 2, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("sesame-oil", 1, "tsp", undefined, 1),
      ]),
      pantry.water(300),
    ],
    steps: [
      {
        id: "aromatics",
        template:
          "Heat {oil.name} in a pot over medium heat. Fry {garlic.name}, {ginger.name}, and {lemongrass.name} for 2 minutes until fragrant.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede orta ateşte ısıtın. {garlic.name}, {ginger.name} ve {lemongrass.name} kokusu çıkana kadar 2 dakika kavurun.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Pour in {coconut.name} and {water.name}. Bring to a gentle simmer and cook for 10 minutes to infuse the flavour.",
        translations: {
          tr: {
            template:
              "{coconut.name} ve {water.name} ekleyin. Hafifçe kaynatın ve lezzetin sinmesi için 10 dakika pişirin.",
          },
        },
      },
      {
        id: "add-protein",
        template:
          "Remove the {lemongrass.name} stalks. Add {protein.name} and {sauce.name} and simmer for 5 minutes.",
        translations: {
          tr: {
            template:
              "{lemongrass.name} saplarını çıkarın. {protein.name} ve {sauce.name} ekleyip 5 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {vegetable.name} if using, cook 1–2 minutes until wilted, then add {acid.name}. Ladle into bowls and scatter {finish.name} over the top.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {vegetable.name} ekleyip solana kadar 1-2 dakika pişirin, ardından {acid.name} ekleyin. Kaselere paylaştırın ve üzerine {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fat", "aroma", "umami", "acid", "protein"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Limon otu & {protein.name} hindistancevizi çorbası",
      },
    },
  },
  {
    id: "barley-mushroom-soup",
    nameTemplate: "{grain.name} & {mushroom.name} soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 40,
    slots: [
      slot("grain", "Grain", true, "barley", [
        option("barley", 120, "g", "rinsed", 3, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
        option("farro", 120, "g", "rinsed", 2, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("mushroom", "Mushroom", true, "mushroom", [
        option("mushroom", 250, "g", "sliced", 3, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("sauce", "Savory depth", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("herb", "Herb", false, "parsley", [
        option("parsley", 10, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("dill", 10, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
      pantry.water(900),
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a pot over medium heat. Cook {onion.name} for 5 minutes until softened, then add {garlic.name} and cook 1 minute more.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin, sonra {garlic.name} ekleyip 1 dakika daha pişirin.",
          },
        },
      },
      {
        id: "mushrooms",
        template:
          "Turn the heat to high and add {mushroom.name}. Cook for 5 minutes without stirring, until golden.",
        translations: {
          tr: {
            template:
              "Ateşi yükseltin ve {mushroom.name} ekleyin. Karıştırmadan altın rengi olana kadar 5 dakika pişirin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {grain.name} and {water.name}. Bring to a boil, then reduce heat, cover, and simmer for 25–30 minutes until the grain is tender.",
        translations: {
          tr: {
            template:
              "{grain.name} ve {water.name} ekleyin. Kaynatın, ardından ateşi kısın, kapatın ve tane yumuşayana kadar 25-30 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {sauce.name}, season with {salt.name} and {black-pepper.name}, and ladle into bowls. Scatter {herb.name} over the top if using.",
        translations: {
          tr: {
            template:
              "{sauce.name} karıştırın, {salt.name} ve {black-pepper.name} ile tatlandırın ve kaselere paylaştırın. Kullanıyorsanız üzerine {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "starch", "aroma", "fat", "bulk"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{grain.name} & {mushroom.name} çorbası",
      },
    },
  },
  {
    id: "shakshuka",
    nameTemplate: "Shakshuka",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("egg", "Eggs", true, "egg", [
        option(
          "egg",
          4,
          "piece",
          undefined,
          1,
          {
            safetyCue:
              "Cover and cook on low heat for 5 to 7 minutes until the whites are just set but the yolks are still runny",
          },
          {
            tr: {
              instructionValues: {
                safetyCue:
                  "Kapağını kapatıp kısık ateşte, beyazlar yeni pişmiş ama sarılar hâlâ sulu kalana kadar 5 ila 7 dakika pişirin",
              },
            },
          },
        ),
      ]),
      slot("tomato", "Tomato base", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("pepper", "Pepper", true, "bell-pepper", [
        option("bell-pepper", 1, "piece", "diced", 3, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
        option("red-pepper-paste", 1, "tbsp", undefined, 2),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely chopped", 3, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely chopped", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("paste", "Tomato paste", true, "tomato-paste", [
        option("tomato-paste", 1, "tbsp"),
      ]),
      slot("herb", "Herb", false, "parsley", [
        option("parsley", 10, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("dill", 10, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("bread", "Bread for serving", false, "flatbread", [
        option("flatbread", 2, "piece", "warmed", 3, undefined, {
          tr: { preparation: "ısıtılmış" },
        }),
        option("tortillas", 2, "piece", "warmed", 1, undefined, {
          tr: { preparation: "ısıtılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a wide, deep pan over medium heat. Cook {onion.name} for 5 minutes until soft, then add {garlic.name} and {spice.name} and stir for 1 minute.",
        translations: {
          tr: {
            template:
              "{oil.name} geniş, derin bir tavada orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin, ardından {garlic.name} ve {spice.name} ekleyip 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "add-pepper",
        template:
          "Add {pepper.name} and cook for 3 minutes, then stir in {paste.name} and cook 1 minute more.",
        translations: {
          tr: {
            template:
              "{pepper.name} ekleyip 3 dakika pişirin, ardından {paste.name} karıştırın ve 1 dakika daha pişirin.",
          },
        },
      },
      {
        id: "simmer-sauce",
        template:
          "Add {tomato.name} and a good pinch of {salt.name}. Simmer for 10 minutes, stirring occasionally, until the sauce thickens.",
        translations: {
          tr: {
            template:
              "{tomato.name} ve bolca {salt.name} ekleyin. Arada karıştırarak sos koyulaşana kadar 10 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "eggs",
        template:
          "Make 4 wells in the sauce and crack an {egg.name} into each. {egg.safetyCue}.",
        translations: {
          tr: {
            template:
              "Sosta 4 çukur açın ve her birine bir {egg.name} kırın. {egg.safetyCue}.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} over the top if using and bring the pan to the table. Serve with {bread.name} if using.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız üzerine {herb.name} serpin ve tavayı sofraya getirin. Kullanıyorsanız {bread.name} ile servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "acid", "umami", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Shakshuka",
      },
    },
  },
  {
    id: "miso-noodle-soup",
    nameTemplate: "Miso noodle soup with {protein.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("noodle", "Noodles", true, "noodles", [
        option("noodles", 180, "g", "cooked separately", 1, undefined, {
          tr: { preparation: "ayrıca haşlanmış" },
        }),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 250, "g", "pressed and cubed", 3, undefined, {
          tr: { preparation: "suyu sıkılmış ve küp doğranmış" },
        }),
        option("edamame", 180, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu soyulmuş" },
        }),
        option("chickpeas", 200, "g", "drained", 1, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("vegetable", "Vegetable", true, "bok-choy", [
        option("bok-choy", 200, "g", "halved", 3, undefined, {
          tr: { preparation: "ikiye bölünmüş" },
        }),
        option("spinach", 80, "g", undefined, 2),
        option("broccoli", 200, "g", "cut into small florets", 1, undefined, {
          tr: { preparation: "küçük çiçekler halinde kesilmiş" },
        }),
      ]),
      slot("miso", "Miso paste", true, "miso", [option("miso", 2, "tbsp")]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely sliced", 1, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "scallion", [
        option("scallion", 2, "piece", "sliced", 3, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("sesame-seeds", 1, "tbsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.water(700),
    ],
    steps: [
      {
        id: "broth",
        template:
          "Heat {oil.name} in a pot over medium heat. Fry {garlic.name} and {ginger.name} for 1 minute. Add {water.name} and bring to a simmer.",
        translations: {
          tr: {
            template:
              "{oil.name} bir tencerede orta ateşte ısıtın. {garlic.name} ve {ginger.name} 1 dakika kavurun. {water.name} ekleyip kaynatın.",
          },
        },
      },
      {
        id: "dissolve-miso",
        template:
          "Ladle a little hot broth into a small bowl and whisk in {miso.name} until smooth, then pour back into the pot. Add {sauce.name}.",
        translations: {
          tr: {
            template:
              "Küçük bir kaseye biraz sıcak et suyu alıp içine {miso.name} pürüzsüz olana kadar çırpın, ardından tencereye geri dökün. {sauce.name} ekleyin.",
          },
        },
      },
      {
        id: "add-protein-veg",
        template:
          "Add {protein.name} and {vegetable.name} to the broth and simmer for 3–4 minutes until warmed through and the vegetables are just tender.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {vegetable.name} et suyuna ekleyin ve ısınana ve sebzeler yumuşayana kadar 3-4 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Divide {noodle.name} between bowls, ladle the broth and {protein.name} over the top, and scatter {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{noodle.name} kaselere paylaştırın, üzerine et suyu ve {protein.name} dökün ve kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "starch", "protein", "aroma", "fat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ile miso erişte çorbası",
      },
    },
  },
  {
    id: "spiced-red-lentil-soup",
    nameTemplate: "Spiced red lentil soup",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("lentil", "Red lentils", true, "lentils", [
        option("lentils", 250, "g", "rinsed", 1, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely chopped", 3, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely chopped", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("paste", "Tomato paste", false, "tomato-paste", [
        option("tomato-paste", 1, "tbsp"),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lime", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("herb", "Herb finish", false, "parsley", [
        option("parsley", 10, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("dill", 10, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(900),
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a large pot over medium heat. Cook {onion.name} for 5 minutes until soft, then add {garlic.name}, {ginger.name}, {spice.name}, and {paste.name} if using. Stir for 1 minute.",
        translations: {
          tr: {
            template:
              "{oil.name} büyük bir tencerede orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin, ardından {garlic.name}, {ginger.name}, {spice.name} ve kullanıyorsanız {paste.name} ekleyin. 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {lentil.name} and {water.name} with a good pinch of {salt.name}. Bring to a boil, skim any foam, then reduce heat and simmer for 20–25 minutes until the lentils are very soft and beginning to dissolve.",
        translations: {
          tr: {
            template:
              "{lentil.name} ve {water.name} ile bir tutam {salt.name} ekleyin. Kaynatın, oluşan köpüğü alın, ardından ateşi kısıp mercimekler çok yumuşayıp dağılmaya başlayana kadar 20-25 dakika pişirin.",
          },
        },
      },
      {
        id: "blend-finish",
        template:
          "Use a stick blender to partially blend the soup — leave it chunky or go smooth, as you like. Stir in {acid.name} and adjust seasoning.",
        translations: {
          tr: {
            template:
              "El blenderiyle çorbayı kısmen çekin — ister parçalı bırakın ister pürüzsüz hale getirin. {acid.name} karıştırın ve tadını ayarlayın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Ladle into bowls and scatter {herb.name} if using. A drizzle of extra {oil.name} on top is optional but good.",
        translations: {
          tr: {
            template:
              "Kaselere paylaştırın ve kullanıyorsanız {herb.name} serpin. Üzerine biraz daha {oil.name} gezdirmek isteğe bağlı ama güzel olur.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "aroma", "acid", "bulk"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Baharatlı kırmızı mercimek çorbası",
      },
    },
  },
  {
    id: "smoky-bean-chili",
    nameTemplate: "Smoky {bean.name} chili",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 20,
    totalMinutes: 45,
    slots: [
      slot("bean", "Beans", true, "black-beans", [
        option("black-beans", 400, "g", "drained and rinsed", 3, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
        option("chickpeas", 400, "g", "drained and rinsed", 2, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
        option("lentils", 250, "g", "rinsed", 1, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("pepper", "Pepper", true, "bell-pepper", [
        option("bell-pepper", 1, "piece", "diced", 3, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
        option("red-pepper-paste", 1, "tbsp", undefined, 2),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("paste", "Tomato paste", true, "tomato-paste", [
        option("tomato-paste", 1, "tbsp"),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely diced", 2, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "tsp", undefined, 1),
      ]),
      slot("acid", "Acid finish", false, "lime", [
        option("lime", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lemon", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("finish", "Finish", false, "avocado", [
        option("avocado", 1, "piece", "sliced", 3, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("scallion", 2, "piece", "sliced", 2, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(200),
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a large heavy pot over medium heat. Cook {onion.name} for 5 minutes until soft. Add {garlic.name} and {spice.name} and stir for 1 minute.",
        translations: {
          tr: {
            template:
              "{oil.name} büyük ve ağır bir tencerede orta ateşte ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin. {garlic.name} ve {spice.name} ekleyip 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Add {pepper.name} and cook 2 minutes, then stir in {paste.name}. Add {tomato.name} and cook for 5 minutes, breaking it down with a spoon.",
        translations: {
          tr: {
            template:
              "{pepper.name} ekleyip 2 dakika pişirin, ardından {paste.name} karıştırın. {tomato.name} ekleyip kaşıkla ezerek 5 dakika pişirin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {bean.name} and {water.name} with a good pinch of {salt.name}. Bring to a boil, then reduce heat and simmer uncovered for 20–25 minutes until thick, stirring occasionally.",
        translations: {
          tr: {
            template:
              "{bean.name} ve {water.name} ile bir tutam {salt.name} ekleyin. Kaynatın, ardından ateşi kısıp kapağı açık şekilde ara sıra karıştırarak koyulaşana kadar 20-25 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze in {acid.name} if using and adjust seasoning. Serve topped with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {acid.name} sıkın ve tadını ayarlayın. Kullanıyorsanız üzerine {finish.name} koyarak servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "bulk", "umami", "heat", "acid"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Dumanlı {bean.name} chilisi",
      },
    },
  },
  {
    id: "ratatouille-stew",
    nameTemplate: "Slow-cooked {eggplant.name} & {vegetable.name} stew",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 20,
    totalMinutes: 50,
    slots: [
      slot("eggplant", "Eggplant", true, "eggplant", [
        option("eggplant", 1, "piece", "cut into 2 cm cubes", 1, undefined, {
          tr: { preparation: "2 cm küp doğranmış" },
        }),
      ]),
      slot("vegetable", "Second vegetable", true, "zucchini", [
        option("zucchini", 2, "piece", "cut into 2 cm chunks", 3, undefined, {
          tr: { preparation: "2 cm parçalar halinde kesilmiş" },
        }),
        option("bell-pepper", 2, "piece", "roughly diced", 2, undefined, {
          tr: { preparation: "iri küp doğranmış" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 500, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely diced", 2, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 4, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("dill", 10, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("spice", "Spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("acid", "Acid", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lime", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 4, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "salt-eggplant",
        template:
          "Toss {eggplant.name} with a generous pinch of {salt.name}, spread on a plate, and set aside 10 minutes to draw out moisture. Pat dry.",
        translations: {
          tr: {
            template:
              "{eggplant.name} bol bir tutam {salt.name} ile karıştırın, bir tabağa yayın ve nemini çekmesi için 10 dakika bekletin. Kurulayın.",
          },
        },
      },
      {
        id: "sear",
        template:
          "Heat half the {oil.name} in a wide heavy pot over medium-high heat. Sear {eggplant.name} in a single layer, turning occasionally, until golden on most sides, about 5 minutes. Remove to a plate.",
        translations: {
          tr: {
            template:
              "{oil.name} yarısını geniş ve ağır bir tencerede orta-yüksek ateşte ısıtın. {eggplant.name} tek katman halinde, ara sıra çevirerek çoğu yüzü altın rengi olana kadar yaklaşık 5 dakika kızartın. Bir tabağa alın.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Add remaining {oil.name} to the pot and lower heat to medium. Cook {onion.name} for 5 minutes until soft. Add {garlic.name} and {spice.name} if using, stir 1 minute.",
        translations: {
          tr: {
            template:
              "Kalan {oil.name} tencereye ekleyip ateşi orta seviyeye düşürün. {onion.name} yumuşayana kadar 5 dakika pişirin. {garlic.name} ve kullanıyorsanız {spice.name} ekleyip 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "stew",
        template:
          "Add {tomato.name} and {vegetable.name}, season with {salt.name} and {black-pepper.name}, and bring to a simmer. Return {eggplant.name} to the pot. Cook uncovered over low heat for 25–30 minutes, stirring occasionally, until all vegetables are very tender and the sauce is thick.",
        translations: {
          tr: {
            template:
              "{tomato.name} ve {vegetable.name} ekleyin, {salt.name} ve {black-pepper.name} ile tatlandırın ve kaynatın. {eggplant.name} tencereye geri koyun. Kapağı açık şekilde kısık ateşte, ara sıra karıştırarak tüm sebzeler çok yumuşayıp sos koyulaşana kadar 25-30 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze in {acid.name} if using, scatter {herb.name} over the top, and serve with bread or over grains.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {acid.name} sıkın, üzerine {herb.name} serpin ve ekmekle ya da tahıllar üzerinde servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "umami", "aroma", "fat", "acid"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Ağır ateşte pişmiş {eggplant.name} & {vegetable.name} yahnisi",
      },
    },
  },
  {
    id: "congee",
    nameTemplate: "{protein.name} & ginger congee",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 3,
    activeMinutes: 10,
    totalMinutes: 65,
    slots: [
      slot("rice", "Rice", true, "rice", [
        option("rice", 150, "g", "rinsed", 1, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option(
          "tofu",
          200,
          "g",
          "pressed and cut into small cubes",
          3,
          undefined,
          {
            tr: {
              preparation: "suyu sıkılmış ve küçük küpler halinde kesilmiş",
            },
          },
        ),
        option("edamame", 150, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu soyulmuş" },
        }),
        option("chickpeas", 200, "g", "drained", 1, undefined, {
          tr: { preparation: "suyu süzülmüş" },
        }),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 30, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely sliced", 1, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("oil", "Finishing oil", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "scallion", [
        option("scallion", 2, "piece", "thinly sliced", 3, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
        option("sesame-seeds", 1, "tbsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.salt,
      pantry.water(1200),
    ],
    steps: [
      {
        id: "simmer",
        template:
          "Combine {rice.name}, {water.name}, {ginger.name}, and {garlic.name} in a large pot. Bring to a boil, then reduce heat to the lowest setting and simmer uncovered, stirring occasionally, for 45–50 minutes until the rice has completely broken down into a thick, creamy porridge.",
        translations: {
          tr: {
            template:
              "{rice.name}, {water.name}, {ginger.name} ve {garlic.name} büyük bir tencerede birleştirin. Kaynatın, ardından ateşi en düşük seviyeye indirip kapağı açık şekilde, ara sıra karıştırarak pirinç tamamen dağılıp koyu, kremamsı bir lapa haline gelene kadar 45-50 dakika pişirin.",
          },
        },
      },
      {
        id: "add-protein",
        template:
          "Stir in {protein.name} and {sauce.name}. Simmer 5 minutes more until protein is heated through. Taste and adjust salt.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {sauce.name} karıştırın. Protein iyice ısınana kadar 5 dakika daha pişirin. Tadına bakıp tuzunu ayarlayın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Ladle into bowls. Drizzle {oil.name} over each bowl and top with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "Kaselere paylaştırın. Her kasenin üzerine {oil.name} gezdirin ve kullanıyorsanız {finish.name} ile üzerini süsleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "bulk", "umami", "aroma", "fat"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve zencefilli pirinç lapası",
      },
    },
  },
  {
    id: "minestrone",
    nameTemplate: "Minestrone with {legume.name} & {pasta.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 20,
    totalMinutes: 50,
    slots: [
      slot("legume", "Legume", true, "chickpeas", [
        option("chickpeas", 400, "g", "drained and rinsed", 3, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
        option("black-beans", 400, "g", "drained and rinsed", 2, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
        option("lentils", 200, "g", "rinsed", 1, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("pasta", "Pasta or grain", true, "spaghetti", [
        option("spaghetti", 80, "g", "broken into short pieces", 3, undefined, {
          tr: { preparation: "kısa parçalar halinde kırılmış" },
        }),
        option("barley", 100, "g", "rinsed", 2, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("vegetable", "Soft vegetable", true, "zucchini", [
        option("zucchini", 1, "piece", "diced", 3, undefined, {
          tr: { preparation: "küp doğranmış" },
        }),
        option(
          "cauliflower",
          200,
          "g",
          "cut into small florets",
          2,
          undefined,
          { tr: { preparation: "küçük çiçekler halinde kesilmiş" } },
        ),
        option("spinach", 80, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("root", "Aromatic vegetable", true, "carrot", [
        option("carrot", 2, "piece", "finely diced", 2, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("celery", 2, "piece", "finely diced", 1, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("tomato-paste", 2, "tbsp", undefined, 1),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("leek", 1, "piece", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("herb", "Herb finish", true, "basil", [
        option("basil", 10, "g", "leaves torn", 3, undefined, {
          tr: { preparation: "yaprakları koparılmış" },
        }),
        option("parsley", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
      pantry.water(1100),
    ],
    steps: [
      {
        id: "soffritto",
        template:
          "Heat {oil.name} in a large heavy pot over medium heat. Cook {onion.name} and {root.name} together for 8 minutes, stirring occasionally, until soft and starting to colour.",
        translations: {
          tr: {
            template:
              "{oil.name} büyük ve ağır bir tencerede orta ateşte ısıtın. {onion.name} ve {root.name} birlikte, ara sıra karıştırarak yumuşayıp renklenmeye başlayana kadar 8 dakika pişirin.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Add {garlic.name}, stir for 1 minute. Add {tomato.name} and cook 5 minutes, breaking it down with a spoon.",
        translations: {
          tr: {
            template:
              "{garlic.name} ekleyip 1 dakika karıştırın. {tomato.name} ekleyip kaşıkla ezerek 5 dakika pişirin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Pour in {water.name}, add {legume.name}, and season well with {salt.name} and {black-pepper.name}. Bring to a boil, then simmer over medium-low heat for 15 minutes.",
        translations: {
          tr: {
            template:
              "{water.name} dökün, {legume.name} ekleyin ve {salt.name} ile {black-pepper.name} ile iyice tatlandırın. Kaynatın, ardından orta-kısık ateşte 15 dakika pişirin.",
          },
        },
      },
      {
        id: "finish-cook",
        template:
          "Add {pasta.name} and {vegetable.name}. Cook a further 10–12 minutes until the pasta or grain is tender and the vegetables are soft. Taste and adjust seasoning.",
        translations: {
          tr: {
            template:
              "{pasta.name} ve {vegetable.name} ekleyin. Makarna ya da tahıl yumuşayana ve sebzeler pişene kadar 10-12 dakika daha pişirin. Tadına bakıp baharatını ayarlayın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Ladle into bowls, scatter {herb.name} over the top, and finish with a drizzle of {oil.name}.",
        translations: {
          tr: {
            template:
              "Kaselere paylaştırın, üzerine {herb.name} serpin ve biraz {oil.name} gezdirerek tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "starch", "umami", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{legume.name} & {pasta.name} ile minestrone",
      },
    },
  },
  {
    id: "caponata",
    nameTemplate: "Sweet-sour {eggplant.name} caponata with {olive.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 30,
    totalMinutes: 50,
    slots: [
      slot("eggplant", "Eggplant", true, "eggplant", [
        option("eggplant", 2, "piece", "cut into 2 cm cubes", 1, undefined, {
          tr: { preparation: "2 cm küp doğranmış" },
        }),
      ]),
      slot("olive", "Olives", true, "olives", [
        option("olives", 80, "g", "pitted and halved", 1, undefined, {
          tr: { preparation: "çekirdekleri çıkarılmış ve ikiye bölünmüş" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("red-onion", 1, "piece", "finely diced", 2, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
      ]),
      slot("celery", "Celery", true, "celery", [
        option("celery", 2, "piece", "thinly sliced", 1, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("sour", "Sour element", true, "red-wine-vinegar", [
        option("red-wine-vinegar", 2, "tbsp"),
      ]),
      slot("sweet", "Sweet element", true, "maple-syrup", [
        option("maple-syrup", 1, "tbsp", undefined, 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("enrichment", "Enrichment", false, "sun-dried-tomato", [
        option("sun-dried-tomato", 40, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 4, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "salt-eggplant",
        template:
          "Toss {eggplant.name} with a generous pinch of {salt.name}, spread on paper towel and set aside 10 minutes. Pat dry.",
        translations: {
          tr: {
            template:
              "{eggplant.name} bol bir tutam {salt.name} ile karıştırın, kağıt havlu üzerine yayıp 10 dakika bekletin. Kurulayın.",
          },
        },
      },
      {
        id: "fry-eggplant",
        template:
          "Heat 3 tbsp {oil.name} in a wide heavy pan over medium-high heat. Fry {eggplant.name} in batches, turning occasionally, until golden on all sides, about 6–8 minutes total. Remove to a plate.",
        translations: {
          tr: {
            template:
              "Geniş ve ağır bir tavada 3 yemek kaşığı {oil.name} orta-yüksek ateşte ısıtın. {eggplant.name} parti parti, ara sıra çevirerek her tarafı altın rengi olana kadar toplam 6-8 dakika kızartın. Bir tabağa alın.",
          },
        },
      },
      {
        id: "soffritto",
        template:
          "Lower the heat to medium. Add the remaining {oil.name} and cook {onion.name} and {celery.name} together for 6 minutes until soft. Add {garlic.name} and stir 1 minute.",
        translations: {
          tr: {
            template:
              "Ateşi orta seviyeye düşürün. Kalan {oil.name} ekleyip {onion.name} ve {celery.name} birlikte yumuşayana kadar 6 dakika pişirin. {garlic.name} ekleyip 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "stew",
        template:
          "Add {tomato.name} and {enrichment.name} if using. Simmer 10 minutes, stirring occasionally, until the tomato collapses. Return {eggplant.name} to the pan along with {olive.name}, {sour.name}, and {sweet.name}. Stir everything together and simmer 8–10 minutes more until the sauce is thick and the flavour is distinctly sweet-sour. Season with {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{tomato.name} ve kullanıyorsanız {enrichment.name} ekleyin. Domates dağılana kadar ara sıra karıştırarak 10 dakika pişirin. {eggplant.name} tavaya geri koyun, {olive.name}, {sour.name} ve {sweet.name} ile birlikte karıştırın ve sos koyulaşıp tat belirgin şekilde tatlı-ekşi olana kadar 8-10 dakika daha pişirin. {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "rest-serve",
        template:
          "Remove from heat and rest at least 10 minutes — caponata improves as it cools. Serve at room temperature on bread, alongside grains, or as a vegetable side.",
        translations: {
          tr: {
            template:
              "Ateşten alıp en az 10 dakika dinlendirin — kaponata soğudukça lezzetlenir. Oda sıcaklığında ekmekle, tahıllarla veya sebze yanı olarak servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["acid", "sweet", "umami", "fat", "bulk"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{olive.name} ile tatlı-ekşi {eggplant.name} kaponatası",
      },
    },
  },
  {
    id: "dolmades",
    nameTemplate:
      "Dolmades: stuffed {leaf.name} with {grain.name} & {herb.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 3,
    activeMinutes: 40,
    totalMinutes: 90,
    slots: [
      slot("leaf", "Grape leaves", true, "grape-leaves", [
        option(
          "grape-leaves",
          25,
          "piece",
          "rinsed and patted dry",
          1,
          undefined,
          { tr: { preparation: "yıkanmış ve kurulanmış" } },
        ),
      ]),
      slot("grain", "Filling grain", true, "rice", [
        option("rice", 150, "g", "long-grain, uncooked", 3, undefined, {
          tr: { preparation: "uzun taneli, çiğ" },
        }),
        option("bulgur", 120, "g", "fine-grain, uncooked", 2, undefined, {
          tr: { preparation: "ince taneli, çiğ" },
        }),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "very finely chopped", 3, undefined, {
          tr: { preparation: "çok ince kıyılmış" },
        }),
        option("mint", 20, "g", "very finely chopped", 2, undefined, {
          tr: { preparation: "çok ince kıyılmış" },
        }),
        option("dill", 20, "g", "very finely chopped", 1, undefined, {
          tr: { preparation: "çok ince kıyılmış" },
        }),
      ]),
      slot("aroma", "Dried herb", true, "dried-mint", [
        option("dried-mint", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", "ground", 2, undefined, {
          tr: { preparation: "öğütülmüş" },
        }),
      ]),
      slot("scallion", "Allium", true, "scallion", [
        option("scallion", 4, "piece", "thinly sliced", 3, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
        option("onion", 1, "piece", "very finely minced", 2, undefined, {
          tr: { preparation: "çok ince kıyılmış" },
        }),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 2, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lime", 2, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
      pantry.pepper,
      pantry.water(400),
    ],
    steps: [
      {
        id: "prepare-leaves",
        template:
          "Rinse {leaf.name} well and pat dry. Trim any tough stems. Lay out one leaf at a time, shiny-side-down, on a work surface.",
        translations: {
          tr: {
            template:
              "{leaf.name} iyice yıkayıp kurulayın. Sert sapları kesin. Bir çalışma yüzeyine yapraklardan birini parlak yüzü aşağı bakacak şekilde yerleştirin.",
          },
        },
      },
      {
        id: "make-filling",
        template:
          "Combine {grain.name} (uncooked), {herb.name}, {aroma.name}, and {scallion.name} in a bowl. Stir in 1 tbsp {oil.name}, half the {acid.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{grain.name} (çiğ), {herb.name}, {aroma.name} ve {scallion.name} bir kasede birleştirin. 1 yemek kaşığı {oil.name}, {acid.name}'nin yarısı, {salt.name} ve {black-pepper.name} ekleyip karıştırın.",
          },
        },
      },
      {
        id: "roll",
        template:
          "Place a heaped teaspoon of filling near the base of each {leaf.name}. Fold in the sides, then roll firmly into a tight small cylinder — rolls should be compact so the {grain.name} has room to expand. Repeat with remaining leaves.",
        translations: {
          tr: {
            template:
              "Her {leaf.name} tabanının yakınına tepeleme bir çay kaşığı harç koyun. Kenarları içe katlayıp sıkıca küçük bir silindir halinde sarın — {grain.name} genişleyebilsin diye sarmalar sıkı olmalı. Kalan yapraklarla tekrarlayın.",
          },
        },
      },
      {
        id: "arrange-poach",
        template:
          "Line the base of a wide heavy pot with a layer of flat {leaf.name} scraps or lemon slices. Arrange stuffed rolls tightly, seam-side-down, in neat snug layers. Drizzle remaining {oil.name} and {acid.name} over the rolls, then pour in {water.name} — just enough to barely cover.",
        translations: {
          tr: {
            template:
              "Geniş ve ağır bir tencerenin tabanını düz {leaf.name} artıkları veya limon dilimleriyle kaplayın. Sarmaları dikiş tarafı aşağı bakacak şekilde sıkıca, düzenli katmanlar halinde dizin. Kalan {oil.name} ve {acid.name} sarmaların üzerine gezdirin, ardından ancak örtecek kadar {water.name} dökün.",
          },
        },
      },
      {
        id: "cook",
        template:
          "Place an inverted plate on top of the rolls to keep them in place. Bring to a gentle boil, then cover tightly and simmer over low heat for 45 minutes until the {grain.name} inside is fully cooked and tender.",
        translations: {
          tr: {
            template:
              "Sarmaların yerinde durması için üzerlerine ters çevrilmiş bir tabak koyun. Hafifçe kaynatın, ardından sıkıca kapatıp içindeki {grain.name} tamamen pişip yumuşayana kadar kısık ateşte 45 dakika pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Remove from heat and rest 10 minutes. Carefully lift the rolls out and arrange on a plate. Serve warm or at room temperature.",
        translations: {
          tr: {
            template:
              "Ateşten alıp 10 dakika dinlendirin. Sarmaları dikkatlice çıkarıp bir tabağa dizin. Ilık veya oda sıcaklığında servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "aroma", "acid", "fat", "fresh"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Dolma: {grain.name} ve {herb.name} ile doldurulmuş {leaf.name}",
      },
    },
  },
  {
    id: "harira",
    nameTemplate: "Harira with {lentil.name} & {legume.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 20,
    totalMinutes: 60,
    slots: [
      slot("lentil", "Lentils", true, "lentils", [
        option("lentils", 120, "g", "rinsed", 1, undefined, {
          tr: { preparation: "yıkanmış" },
        }),
      ]),
      slot("legume", "Chickpeas", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and rinsed", 1, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("tomato-paste", 3, "tbsp", undefined, 2),
      ]),
      slot("spice", "Warm spice blend", true, "cinnamon", [
        option("cinnamon", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", "ground", 2, undefined, {
          tr: { preparation: "öğütülmüş" },
        }),
        option("ginger", 15, "g", "freshly grated", 1, undefined, {
          tr: { preparation: "taze rendelenmiş" },
        }),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("leek", 1, "piece", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("pasta", "Pasta (optional thickener)", false, "spaghetti", [
        option("spaghetti", 60, "g", "broken into short pieces", 1, undefined, {
          tr: { preparation: "kısa parçalar halinde kırılmış" },
        }),
      ]),
      slot("acid", "Acid finish", true, "lemon", [
        option("lemon", 2, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
        option("lime", 2, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu sıkılmış" },
        }),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      pantry.salt,
      pantry.pepper,
      pantry.water(1400),
    ],
    steps: [
      {
        id: "soffritto",
        template:
          "Heat {oil.name} in a large pot over medium heat. Cook {onion.name} for 6 minutes until soft and lightly golden. Add {garlic.name} and {spice.name}, and stir 1 minute until very fragrant.",
        translations: {
          tr: {
            template:
              "{oil.name} büyük bir tencerede orta ateşte ısıtın. {onion.name} yumuşayıp hafif altın rengi olana kadar 6 dakika pişirin. {garlic.name} ve {spice.name} ekleyip çok kokulu olana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Add {tomato.name} and cook 5 minutes, stirring occasionally, until the tomato collapses and thickens.",
        translations: {
          tr: {
            template:
              "{tomato.name} ekleyip domates dağılıp koyulaşana kadar ara sıra karıştırarak 5 dakika pişirin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {lentil.name}, {legume.name}, and {water.name}. Season with {salt.name} and {black-pepper.name}. Bring to a boil, then reduce heat and simmer 30 minutes until the lentils are completely tender.",
        translations: {
          tr: {
            template:
              "{lentil.name}, {legume.name} ve {water.name} ekleyin. {salt.name} ve {black-pepper.name} ile tatlandırın. Kaynatın, ardından ateşi kısıp mercimekler tamamen yumuşayana kadar 30 dakika pişirin.",
          },
        },
      },
      {
        id: "add-pasta",
        template:
          "If using {pasta.name}, stir it into the soup now and cook a further 8–10 minutes until tender. The soup should be very thick — add a splash of water if it becomes too dense.",
        translations: {
          tr: {
            template:
              "{pasta.name} kullanıyorsanız şimdi çorbaya ekleyip yumuşayana kadar 8-10 dakika daha pişirin. Çorba çok koyu olmalı — fazla koyulaşırsa bir tutam su ekleyin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Squeeze {acid.name} into the soup and stir well. Taste and adjust seasoning — harira should be warm, rich, and distinctly lemon-forward.",
        translations: {
          tr: {
            template:
              "{acid.name} çorbaya sıkıp iyice karıştırın. Tadına bakıp baharatını ayarlayın — harira sıcak, zengin ve belirgin şekilde limonlu olmalı.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "aroma", "acid", "bulk"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{lentil.name} & {legume.name} ile harira",
      },
    },
  },
  {
    id: "ribollita",
    nameTemplate: "Ribollita with {bread.name} & {green.name}",
    foodType: "soup-stew",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 25,
    totalMinutes: 55,
    slots: [
      slot("bean", "White bean", true, "white-beans", [
        option("white-beans", 400, "g", "drained and rinsed", 3, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
        option("chickpeas", 400, "g", "drained and rinsed", 2, undefined, {
          tr: { preparation: "suyu süzülmüş ve yıkanmış" },
        }),
      ]),
      slot("bread", "Stale bread", true, "bread", [
        option(
          "bread",
          150,
          "g",
          "stale, torn into rough 4 cm chunks",
          1,
          undefined,
          {
            tr: {
              preparation: "bayat, kabaca 4 cm parçalar halinde yırtılmış",
            },
          },
        ),
      ]),
      slot("green", "Leafy green", true, "spinach", [
        option("spinach", 150, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
      ]),
      slot("root", "Aromatic vegetable", true, "carrot", [
        option("carrot", 2, "piece", "finely diced", 3, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option("celery", 2, "piece", "finely diced", 2, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
        option(
          "potato",
          2,
          "piece",
          "peeled and diced into 1 cm cubes",
          1,
          undefined,
          {
            tr: {
              preparation: "kabuğu soyulmuş ve 1 cm küpler halinde doğranmış",
            },
          },
        ),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 1, undefined, {
          tr: { preparation: "ince küp doğranmış" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince kıyılmış" },
        }),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 400, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri doğranmış" },
        }),
        option("tomato-paste", 2, "tbsp", undefined, 2),
      ]),
      slot("herb", "Herb finish", true, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "iri kıyılmış" },
        }),
        option("basil", 10, "g", "leaves torn", 2, undefined, {
          tr: { preparation: "yaprakları koparılmış" },
        }),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 4, "tbsp")]),
      pantry.salt,
      pantry.pepper,
      pantry.water(1000),
    ],
    steps: [
      {
        id: "soffritto",
        template:
          "Heat 3 tbsp {oil.name} in a large heavy pot over medium heat. Cook {onion.name} and {root.name} together for 8 minutes, stirring occasionally, until soft.",
        translations: {
          tr: {
            template:
              "Büyük ve ağır bir tencerede 3 yemek kaşığı {oil.name} orta ateşte ısıtın. {onion.name} ve {root.name} birlikte, ara sıra karıştırarak yumuşayana kadar 8 dakika pişirin.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Add {garlic.name} and stir 1 minute. Add {tomato.name} and cook 5 minutes until it collapses. Add {bean.name}, {green.name}, and {water.name}. Season with {salt.name} and {black-pepper.name}. Bring to a boil, then simmer 20 minutes.",
        translations: {
          tr: {
            template:
              "{garlic.name} ekleyip 1 dakika karıştırın. {tomato.name} ekleyip dağılana kadar 5 dakika pişirin. {bean.name}, {green.name} ve {water.name} ekleyin. {salt.name} ve {black-pepper.name} ile tatlandırın. Kaynatın, ardından 20 dakika pişirin.",
          },
        },
      },
      {
        id: "add-bread",
        template:
          "Stir {bread.name} directly into the soup so the pieces submerge and begin absorbing the broth. Simmer a further 10 minutes, stirring occasionally — the soup will thicken considerably as the bread breaks down. If it becomes too thick, add a splash of water.",
        translations: {
          tr: {
            template:
              "{bread.name} doğrudan çorbaya karıştırın, parçalar batıp suyu emmeye başlasın. Ara sıra karıştırarak 10 dakika daha pişirin — ekmek dağıldıkça çorba belirgin şekilde koyulaşacaktır. Çok koyulaşırsa bir tutam su ekleyin.",
          },
        },
      },
      {
        id: "rest-serve",
        template:
          "Taste and adjust seasoning. Remove from heat and rest 5 minutes — ribollita thickens as it stands. Ladle into bowls, scatter {herb.name} over the top, and finish each serving with a drizzle of the remaining {oil.name}.",
        translations: {
          tr: {
            template:
              "Tadına bakıp baharatını ayarlayın. Ateşten alıp 5 dakika dinlendirin — ribollita bekledikçe koyulaşır. Kaselere paylaştırın, üzerine {herb.name} serpin ve kalan {oil.name} gezdirerek tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "bulk", "umami", "fat"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{bread.name} ve {green.name} ile ribollita",
      },
    },
  },
];
